import {ChangeEventHandler, Dispatch, SetStateAction, useEffect, useState} from 'react';
import {
  Flex,
  Text,
  Input,
  Select,
  FormControl,
  FormLabel,
  InputGroup,
  InputRightElement,
  Box,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Heading,
} from '@chakra-ui/react';
import {SearchIcon} from '@chakra-ui/icons';
import {shallow} from 'zustand/shallow';

import {useTranslate} from '@eksab/i18n';

import {useTeamsQuery} from '../useTeamsQuery';
import {positions} from '../../const';
import {useIntl} from 'react-intl';
import {ExpandingSearch, Loader} from '@eksab/components';
import {useLineupStore} from '../useLineupStore';
import {FiltersT} from '../../types';

type Props = {
  filters: FiltersT;
  setFilters: Dispatch<SetStateAction<FiltersT>>;
  isLoading?: boolean;
  competitionId: string;
};

export function Filters({filters, setFilters, competitionId, isLoading}: Props) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const formatNumber = useIntl().formatNumber;

  useEffect(() => {
    mixpanel?.track('Button Clicked', {
      button: 'competition.filter_players',
      filters,
    });
  }, [filters]);

  const t = useTranslate();
  const teamsQuery = useTeamsQuery(competitionId);

  const onChange: ChangeEventHandler<HTMLInputElement | HTMLSelectElement> = (e) =>
    setFilters((old) => ({...old, [e.target.name]: e.target.value}));

  const onCostRangeChange: (value: number[]) => void = (values) =>
    setFilters((old) => ({...old, price_from: values[0], price_to: values[1]}));

  const onQueryChange = (q: string) => setFilters((old) => ({...old, q}));

  return (
    <Box bg="white" pb="2.5">
      <Flex
        pos={['sticky', null, 'static']}
        top="0"
        bg="white"
        zIndex={200}
        w="full"
        justify="space-between"
        align="center"
        gap="5"
        h="fit-content"
        pt="1.5"
        pb="5"
      >
        <Heading
          as="h4"
          display="flex"
          alignItems="center"
          gap="5"
          fontSize={['md', null, 'xl']}
          pos={['absolute', null, 'static']}
          insetStart={isSearchOpen ? '-100%' : '0'}
          transition="inset 0.7s ease-out"
        >
          {t('fantasy.lineup.filters')}

          {isLoading && <Loader size="sm" />}
        </Heading>

        <Flex flex={1} justify="flex-end" visibility={{md: 'hidden'}}>
          <ExpandingSearch
            isSearchOpen={isSearchOpen}
            setIsSearchOpen={setIsSearchOpen}
            query={filters.q ?? ''}
            setQuery={onQueryChange}
            color="black"
          />
        </Flex>
      </Flex>

      <Flex gap="2.5" mb="5">
        <FormControl>
          <FormLabel fontSize="0.75rem" fontWeight="700">
            {t('fantasy.lineup.team')}
          </FormLabel>

          <Select value={filters.team} name="team" onChange={onChange}>
            {teamsQuery.data?.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="0.75rem" fontWeight="700">
            {t('fantasy.lineup.position')}
          </FormLabel>
          <PositionSelect />
        </FormControl>
      </Flex>

      <Box mb="5">
        <Text fontSize="0.75rem" fontWeight="700" mb="2.5">
          {t('fantasy.lineup.cost_range')}
        </Text>

        <Flex justify="space-between">
          <Text fontSize="sm">{t('fantasy.lineup.price-unit', {amount: formatNumber(0)})}</Text>
          <Text fontSize="sm">{t('fantasy.lineup.price-unit', {amount: formatNumber(5)})}</Text>
          <Text fontSize="sm">{t('fantasy.lineup.price-unit', {amount: formatNumber(10)})}</Text>
          <Text fontSize="sm">{t('fantasy.lineup.price-unit', {amount: formatNumber(15)})}</Text>
        </Flex>

        <RangeSlider
          min={0}
          max={15}
          step={1}
          onChange={onCostRangeChange}
          value={[filters.price_from ?? 0, filters.price_to ?? 0]}
        >
          <RangeSliderTrack>
            <RangeSliderFilledTrack bgColor="black" />
          </RangeSliderTrack>
          <RangeSliderThumb index={0} />
          <RangeSliderThumb index={1} />
        </RangeSlider>
      </Box>

      <FormControl display={['none', null, 'block']} mb="5">
        <FormLabel fontSize="0.75rem" fontWeight="700">
          {t('app.actions.search')}
        </FormLabel>

        <InputGroup>
          <Input value={filters.q} name="q" onChange={onChange} />

          <InputRightElement>
            <SearchIcon />
          </InputRightElement>
        </InputGroup>
      </FormControl>
    </Box>
  );
}

export function PositionSelect() {
  const t = useTranslate();
  const {indicatorPosition, setIndicatorPosition} = useLineupStore(
    (state) => ({indicatorPosition: state.indicatorPosition, setIndicatorPosition: state.setIndicatorPosition}),
    shallow,
  );

  const onPositionChange: ChangeEventHandler<HTMLSelectElement> = (e) =>
    setIndicatorPosition({...indicatorPosition, index: 0, position: e.target.value as (typeof positions)[number]});

  return (
    <Select value={indicatorPosition.position} name="position" onChange={onPositionChange}>
      {positions.map((position) => (
        <option key={position} value={position}>
          {t(`fantasy.lineup.${position}`)}
        </option>
      ))}
    </Select>
  );
}
